import { isEmpty, values } from 'lodash'
import humps from 'humps'

import ModifierGroupsViewModel from '../viewmodels/ModifierGroupsViewModel'

import { mapOrderDetails, mapOrder, mapPayments } from '../models/Order'

export default class DataMapper {
  static objectsById = (objects = []) => {
    return objects.reduce((result, object) => {
      return {
        ...result,
        [object.uuid || object.id || object.itemCategoryUuid]: object,
      }
    }, {})
  }

  static baseVariant = (item) => {
    if (!item.variants.length) return []

    return item.variants.map((variant) => ({
      productSku: variant.productSku,
      priceInCents: variant.priceInCents,
      inventoryState: variant.inventoryState,
    }))
  }

  static baseItem = (item) => ({
    canEnterNotes: item.canEnterNotes ?? false,
    category: item.category,
    defaultPriceInCents: item.defaultPriceInCents,
    barcode: item.barcode ?? null,
    description: item.description ?? '',
    id: item.id,
    isAlcohol: item.isAlcohol ?? false,
    isModifier: item.isModifier ?? false,
    itemCategories: item.itemCategories ?? [],
    itemCategoryUuid: item.itemCategoryUuid,
    itemCategoryUuids: item.itemCategoryUuids ?? [],
    modifier: item.modifier ?? false,
    name: item.name ?? '',
    orderMaxAmount: item.orderMaxAmount ?? 999,
    orderMinCount: item.orderMinCount ?? 0,
    rate: item.rate,
    specialType: item.specialType,
    standMenuUuid: item.standMenuUuid,
    taxable: item.taxable ?? false,
    uuid: item.uuid,
    variants: this.baseVariant(item),
    taxClassUuid: item.taxClassUuid ?? null,
  })

  static modifierGroupsMapper = (modifierGroup = []) => {
    const items = (modifierGroup.items ?? []).map((item) => ({ uuid: item.uuid }))

    return {
      name: modifierGroup.name ?? 'Modifier',
      min: modifierGroup.min ?? null,
      max: modifierGroup.max ?? null,
      requiresSelection: modifierGroup.requiresSelection ?? false,
      sortOrder: modifierGroup.sortOrder ?? 0,
      secondaryPriceThresholdCount: modifierGroup.secondaryPriceThresholdCount ?? 0,
      items,
      defaults: modifierGroup.defaults ?? [],
      featured: modifierGroup.featured ?? [],
      uuid: modifierGroup.uuid,
    }
  }

  static menuItemsById = (items = [], isKiosk = false) => {
    // kiosk items are not getting addressed in this mapper
    if (isKiosk) {
      return this.objectsById(items)
    }

    // only store item properties that are needed
    return items.reduce((result, item) => {
      if (!item) return result
      return {
        ...result,
        [item.uuid]: {
          ...this.baseItem(item),
          modifierGroups: (item.modifierGroups ?? []).map(this.modifierGroupsMapper),
        },
      }
    }, {})
  }

  static itemModifiersMapper = (item) => {
    if (isEmpty(item.modifierGroups)) return

    let result = ModifierGroupsViewModel(item.modifierGroups)
    return result
  }

  static ordersMapper = (orders = []) => {
    const byId = DataMapper.objectsById(orders)
    const reopenedPaymentsById = {}

    values(byId).forEach((tabOrder) => {
      const order = humps.camelizeKeys(tabOrder)

      byId[tabOrder.uuid] = mapOrder(order)

      if (byId[tabOrder.uuid].reopened) {
        const payments = byId[tabOrder.uuid].paymentInfo?.map?.((payment) => mapPayments(payment, order.orderNumber))
        reopenedPaymentsById[tabOrder.uuid] = payments
      }
    })

    return { byId, reopenedPaymentsById }
  }

  static orderDetailsMapper = (orderDetail = {}, items, useTaxClasses) => {
    const order = humps.camelizeKeys(orderDetail)

    return mapOrderDetails(order, items, useTaxClasses)
  }
}
